import { useRef, useState } from 'react';
import { CaretDown, Plus, X } from 'shared/icons';
import { PERIOD_GROUPS } from '../../../periodHelpers';
import { BrandTagPicker } from '../../BrandTagPicker';
import { DateRangePicker } from '../../DateRangePicker';
import { TBtn, useClickOutside } from '../../primitives';
import { FormatStockRuleLabel, HasStockRuleFor, MakeDefaultStockRule, StockRuleEditor } from '../../StockRuleEditor';
import { useTokens } from '../../tokens';
import { PSection, usePresetBuilder } from '../context.jsx';

/** Botão "tracejado + adicionar X" que abre dropdowns inline. */
function AddRow({ label, onClick, refEl }) {
    const T = useTokens();
    return (
        <button
            ref={refEl}
            type="button"
            onClick={onClick}
            style={{
                width: '100%',
                marginTop: 6,
                border: `1px dashed ${T.line}`,
                background: 'transparent',
                color: T.muted,
                padding: '7px 10px',
                fontSize: 11,
                cursor: 'pointer',
                borderRadius: 4,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                outline: 'none',
            }}
        >
            <Plus size={11} /> {label}
        </button>
    );
}

/**
 * Editor de valor para filtros simples (period, brands, tags, ml_stock_lt, bling_stock_lt).
 * Cada tipo tem seu UI próprio:
 *   period → lista agrupada (PERIOD_GROUPS)
 *   brands/tags → multi-select com checkboxes
 *   ml_stock_lt / bling_stock_lt → input numérico
 */
function FilterDropdown({ kind, draft, onChange, brandOpts, tagOpts, onClose }) {
    const T = useTokens();
    const ref = useRef(null);
    useClickOutside(ref, onClose, true);

    const filters = draft?.filters || {};

    if (kind === 'period') {
        // 'custom' troca o conteúdo do dropdown — APENAS o calendário aparece
        // (sem a lista de períodos acima). Visual de popover dedicado, sem o
        // calendário squished dentro do dropdown menor de períodos.
        if (filters.period === 'custom') {
            return (
                <div
                    ref={ref}
                    style={{
                        position: 'absolute',
                        zIndex: 50,
                        top: 'calc(100% + 4px)',
                        right: 0,
                        background: T.bg,
                        border: `1px solid ${T.line}`,
                        boxShadow: '0 6px 18px rgba(0,0,0,0.10)',
                        borderRadius: 6,
                    }}
                >
                    <DateRangePicker
                        from={filters.dateFrom || null}
                        to={filters.dateTo || null}
                        onChange={(f, to) => {
                            onChange({ ...filters, period: 'custom', dateFrom: f, dateTo: to });
                            onClose();
                        }}
                        onClear={() => {
                            onChange({
                                ...filters,
                                dateFrom: undefined,
                                dateTo: undefined,
                            });
                        }}
                    />
                </div>
            );
        }
        return (
            <div
                ref={ref}
                style={{
                    position: 'absolute',
                    zIndex: 50,
                    top: 'calc(100% + 4px)',
                    right: 0,
                    width: 240,
                    maxHeight: 360,
                    overflowY: 'auto',
                    background: T.bg,
                    border: `1px solid ${T.line}`,
                    boxShadow: '0 6px 18px rgba(0,0,0,0.10)',
                    borderRadius: 6,
                    padding: 4,
                }}
            >
                {PERIOD_GROUPS.map((group, gIdx) => (
                    <div
                        key={group.id}
                        style={{
                            borderTop: gIdx > 0 ? `1px solid ${T.line}` : 'none',
                            paddingTop: gIdx > 0 ? 4 : 0,
                            marginTop: gIdx > 0 ? 4 : 0,
                        }}
                    >
                        <div
                            style={{
                                padding: '6px 10px 2px',
                                fontSize: 10,
                                fontWeight: 600,
                                letterSpacing: 0.6,
                                textTransform: 'uppercase',
                                color: T.muted,
                            }}
                        >
                            {group.label}
                        </div>
                        {group.items.map((opt) => {
                            const active = filters.period === opt.id;
                            return (
                                <button
                                    key={opt.id}
                                    type="button"
                                    onClick={() => {
                                        // 'custom' troca o conteúdo do próprio dropdown
                                        // (próxima render entra no branch acima e mostra
                                        // só o calendário). Não fecha nem abre nada novo.
                                        if (opt.id === 'custom') {
                                            onChange({ ...filters, period: 'custom' });
                                            return;
                                        }
                                        onChange({
                                            ...filters,
                                            period: opt.id,
                                            dateFrom: undefined,
                                            dateTo: undefined,
                                        });
                                        onClose();
                                    }}
                                    style={{
                                        display: 'block',
                                        width: '100%',
                                        textAlign: 'left',
                                        padding: '6px 10px',
                                        fontSize: 12,
                                        background: active ? T.bgMid : 'transparent',
                                        color: active ? T.ink : T.inkMid,
                                        border: 'none',
                                        cursor: 'pointer',
                                        outline: 'none',
                                    }}
                                >
                                    {opt.label}
                                </button>
                            );
                        })}
                    </div>
                ))}
            </div>
        );
    }

    if (kind === 'brands' || kind === 'tags') {
        const isBrand = kind === 'brands';
        const opts = isBrand ? brandOpts : tagOpts;
        const selected = Array.isArray(filters[kind]) ? filters[kind] : [];
        const matchAll = !isBrand && filters.tags_match_all === true;
        // Wrapper só pra dar posição absoluta e click-outside — o conteúdo é
        // o BrandTagPicker compartilhado (mesmo usado no Toolbar).
        return (
            <div
                ref={ref}
                style={{
                    position: 'absolute',
                    zIndex: 50,
                    top: 'calc(100% + 4px)',
                    right: 0,
                    background: T.bg,
                    border: `1px solid ${T.line}`,
                    boxShadow: '0 6px 18px rgba(0,0,0,0.10)',
                    borderRadius: 6,
                }}
            >
                <BrandTagPicker
                    kind={isBrand ? 'brand' : 'tag'}
                    options={opts || []}
                    selected={selected}
                    matchAll={matchAll}
                    onToggle={(v) => {
                        const next = selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v];
                        onChange({ ...filters, [kind]: next });
                    }}
                    onClear={() => onChange({ ...filters, [kind]: [] })}
                    onMatchAllChange={
                        !isBrand
                            ? (next) => {
                                  const updated = { ...filters };
                                  if (next === undefined || next === false) {
                                      delete updated.tags_match_all;
                                  } else {
                                      updated.tags_match_all = true;
                                  }
                                  onChange(updated);
                              }
                            : undefined
                    }
                />
            </div>
        );
    }

    if (kind === 'ml_stock_lt' || kind === 'bling_stock_lt') {
        const value = filters[kind] ?? '';
        return (
            <div
                ref={ref}
                style={{
                    position: 'absolute',
                    zIndex: 50,
                    top: 'calc(100% + 4px)',
                    right: 0,
                    width: 180,
                    background: T.bg,
                    border: `1px solid ${T.line}`,
                    boxShadow: '0 6px 18px rgba(0,0,0,0.10)',
                    borderRadius: 6,
                    padding: 10,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 8,
                }}
            >
                <span style={{ fontSize: 11, color: T.muted }}>
                    {kind === 'ml_stock_lt' ? 'Mercado Livre disponível menor que' : 'Estoque Bling menor que'}
                </span>
                <input
                    type="number"
                    min={0}
                    value={value}
                    onChange={(e) =>
                        onChange({
                            ...filters,
                            [kind]: e.target.value === '' ? undefined : Number(e.target.value),
                        })
                    }
                    // biome-ignore lint/a11y/noAutofocus: dropdown abriu pra editar valor — foco direto evita clique extra
                    autoFocus
                    style={{
                        padding: '6px 8px',
                        borderRadius: 4,
                        border: `1px solid ${T.line}`,
                        background: T.bg,
                        color: T.ink,
                        fontSize: 13,
                        fontFamily: T.mono,
                        outline: 'none',
                    }}
                />
                <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                    <TBtn size="xs" variant="ghost" onClick={onClose}>
                        OK
                    </TBtn>
                </div>
            </div>
        );
    }

    return null;
}

/**
 * Wrapper do StockRuleEditor com:
 *   1. Backdrop full-screen (rgba 0.55) pra dar foco visual — todo modal/picker
 *      modal deve ter, padrão da app (decisão do user).
 *   2. Click-outside fecha (via clique no próprio backdrop) chamando o cleanup
 *      do caller (que descarta filtro sem valor).
 *   3. Stop propagation no painel pra cliques internos não fecharem.
 *
 * Position do conteúdo: ancorado ao chip do filtro via portal-style absolute,
 * mantendo o card visualmente "atado" à row de filtro (não centralizado como
 * modal cheio).
 */
function StockRuleDropdownWrapper({ children, onClose }) {
    const T = useTokens();
    return (
        <>
            <button
                type="button"
                aria-label="Fechar editor de filtro de estoque"
                onClick={(e) => {
                    e.stopPropagation();
                    onClose();
                }}
                data-faid="Stock_Rule_Backdrop_PresetBuilder_Operacoes"
                style={{
                    position: 'fixed',
                    inset: 0,
                    background: 'rgba(0,0,0,0.55)',
                    border: 'none',
                    cursor: 'default',
                    padding: 0,
                    zIndex: 49,
                }}
            />
            <div
                style={{
                    position: 'absolute',
                    zIndex: 50,
                    top: 'calc(100% + 4px)',
                    right: 0,
                    background: T.bg,
                    border: `1px solid ${T.line}`,
                    borderRadius: 6,
                    boxShadow: '0 6px 18px rgba(0,0,0,0.10)',
                }}
            >
                {children}
            </div>
        </>
    );
}

/**
 * Filtros fixos = parte de `preset.filters`. Cada filtro vira uma "linha"
 * com label + valor + ações (editar valor inline, remover).
 *
 * Tipos suportados:
 *   period       — uma data ou range custom
 *   brands       — array de strings
 *   tags         — array de strings
 *   stock_rules  — array de regras { provider, depots, op, value }
 *                  (substitui os antigos ml_stock_lt / bling_stock_lt)
 */
function FixedFilters({ draft, onChange, brandOpts, tagOpts, warehouses = [] }) {
    const T = useTokens();
    const [editing, setEditing] = useState(null); // qual filtro está com dropdown aberto
    const [adding, setAdding] = useState(false);
    const addBtnRef = useRef(null);
    const addRef = useRef(null);
    useClickOutside(addRef, () => setAdding(false), adding);

    const f = draft?.filters || {};

    const removeFilter = (item) => {
        // Stock rules: remove pelo índice do array, mantém o array.
        if (item.kind === 'stock_rule') {
            const nextRules = stockRules.filter((_, i) => i !== item.ruleIndex);
            const next = { ...f };
            if (nextRules.length === 0) delete next.stock_rules;
            else next.stock_rules = nextRules;
            onChange(next);
            setEditing((e) => (e === item.id ? null : e));
            return;
        }
        // Demais filtros: remove a key.
        const next = { ...f };
        delete next[item.id];
        onChange(next);
        setEditing((e) => (e === item.id ? null : e));
    };

    const items = [];
    if (f.period) {
        const found = PERIOD_GROUPS.flatMap((g) => g.items).find((it) => it.id === f.period);
        items.push({
            id: 'period',
            label: 'Período',
            value: found?.label || f.period,
        });
    }
    // Mostra chip mesmo quando array vazio — usuário acabou de adicionar o
    // filtro e ainda vai escolher os valores. Antes só mostrava se length>0,
    // resultando em "adicionei Marca e nada apareceu" + impossibilidade de
    // re-adicionar (porque a key brands já existia no filters).
    if (Array.isArray(f.brands)) {
        const cnt = f.brands.length;
        items.push({
            id: 'brands',
            label: 'Marca',
            value: cnt === 0 ? '— selecione —' : cnt === 1 ? f.brands[0] : `${cnt} marcas`,
            empty: cnt === 0,
        });
    }
    if (Array.isArray(f.tags)) {
        const cnt = f.tags.length;
        items.push({
            id: 'tags',
            label: 'Tags',
            value: cnt === 0 ? '— selecione —' : cnt === 1 ? f.tags[0] : `${cnt} tags`,
            empty: cnt === 0,
        });
    }
    // Stock rules — array, cada regra vira um item editável independente.
    // Label do item é formatado pela helper do StockRuleEditor (consistência).
    const stockRules = Array.isArray(f.stock_rules) ? f.stock_rules : [];
    stockRules.forEach((rule, idx) => {
        // value=0 É VÁLIDO ("ML disp. maior que 0" = "tem estoque"); empty só
        // se value não foi definido (rule recém-criada). Sem isso o chip ficava
        // permanentemente accent+itálico mesmo configurado corretamente.
        const hasValue = rule?.value !== undefined && rule?.value !== null;
        items.push({
            id: `stock_rule:${idx}`,
            label: 'Estoque',
            value: FormatStockRuleLabel(rule, warehouses),
            empty: !hasValue,
            kind: 'stock_rule',
            ruleIndex: idx,
        });
    });

    /**
     * Fechar dropdown de edição. Se o filtro foi adicionado mas não recebeu
     * valor (ex: "+ Marca" → fecha sem escolher nenhuma), descarta o filtro —
     * caso contrário o chip "0 marcas" / "— selecione —" ficava encalhado e
     * o usuário tinha que clicar no X pra limpar.
     *
     * Stock rules: descarta se value ainda é null/undefined; outras props
     * (provider, depots) sem value não servem como filtro funcional.
     */
    const closeEditingAndPurgeEmpty = (item) => {
        if (!item) {
            setEditing(null);
            return;
        }
        if (item.kind === 'stock_rule') {
            const rule = stockRules[item.ruleIndex];
            if (!rule || rule.value === undefined || rule.value === null) {
                removeFilter(item);
                return;
            }
        } else if (item.empty) {
            removeFilter(item);
            return;
        }
        setEditing(null);
    };

    const renderEditing = (item) => {
        if (editing !== item.id) return null;
        // Stock rules têm editor próprio (StockRuleEditor) — não vai pelo
        // FilterDropdown genérico que cuida de period/brands/tags.
        if (item.kind === 'stock_rule') {
            const rule = stockRules[item.ruleIndex];
            // Providers disponíveis = não usados por OUTRAS regras (a do próprio
            // índice pode manter o provider atual). Bloqueia "ML×2" simultaneamente.
            const usedByOthers = stockRules.map((r, i) => (i !== item.ruleIndex ? r?.provider : null)).filter(Boolean);
            const available = ['bling', 'ml'].filter((p) => !usedByOthers.includes(p));
            return (
                <StockRuleDropdownWrapper onClose={() => closeEditingAndPurgeEmpty(item)}>
                    <StockRuleEditor
                        rule={rule}
                        warehouses={warehouses}
                        availableProviders={available}
                        onChange={(nextRule) => {
                            const nextRules = [...stockRules];
                            nextRules[item.ruleIndex] = nextRule;
                            onChange({ ...f, stock_rules: nextRules });
                        }}
                    />
                </StockRuleDropdownWrapper>
            );
        }
        return (
            <FilterDropdown
                kind={item.id}
                draft={draft}
                onChange={onChange}
                brandOpts={brandOpts}
                tagOpts={tagOpts}
                onClose={() => closeEditingAndPurgeEmpty(item)}
            />
        );
    };

    // Regra de estoque por provider — só aparece quando NÃO já existe regra
    // daquele provider. Combinação válida: 1 Bling + 1 ML simultâneos.
    const availableToAdd = [
        !f.period && { id: 'period', label: 'Período' },
        !Array.isArray(f.brands) && { id: 'brands', label: 'Marca' },
        !Array.isArray(f.tags) && { id: 'tags', label: 'Tags' },
        !HasStockRuleFor('bling', f.stock_rules) && {
            id: 'stock_rule_bling',
            label: 'Estoque Bling',
            provider: 'bling',
        },
        !HasStockRuleFor('ml', f.stock_rules) && {
            id: 'stock_rule_ml',
            label: 'Estoque Mercado Livre',
            provider: 'ml',
        },
    ].filter(Boolean);

    return (
        <div>
            {items.length === 0 && (
                <div
                    style={{
                        fontSize: 11,
                        color: T.subtle,
                        fontStyle: 'italic',
                        padding: '6px 0 4px',
                    }}
                >
                    Nenhum filtro fixo. Adicione um abaixo.
                </div>
            )}
            {items.map((it) => (
                <div
                    key={it.id}
                    style={{
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        padding: '6px 8px',
                        marginBottom: 4,
                        // Borda accent quando ainda não preencheu — chama atenção pra
                        // ação obrigatória (selecionar marca/tags depois de adicionar).
                        border: `1px solid ${it.empty ? T.accent : T.line}`,
                        borderRadius: 4,
                        background: T.bg,
                    }}
                >
                    <span style={{ fontSize: 10, color: T.muted, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                        {it.label}
                    </span>
                    <button
                        type="button"
                        onClick={() => setEditing(editing === it.id ? null : it.id)}
                        style={{
                            flex: 1,
                            background: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            color: it.empty ? T.accent : T.ink,
                            fontSize: 12,
                            fontStyle: it.empty ? 'italic' : 'normal',
                            textAlign: 'left',
                            padding: 0,
                            outline: 'none',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 4,
                        }}
                    >
                        <span>{it.value}</span>
                        <CaretDown size={10} color={T.muted} />
                    </button>
                    <button
                        type="button"
                        onClick={() => removeFilter(it)}
                        aria-label={`Remover ${it.label}`}
                        style={{
                            background: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            color: T.muted,
                            padding: 2,
                            display: 'inline-flex',
                            alignItems: 'center',
                            outline: 'none',
                        }}
                    >
                        <X size={11} />
                    </button>
                    {renderEditing(it)}
                </div>
            ))}

            {availableToAdd.length > 0 && (
                <div ref={addRef} style={{ position: 'relative' }}>
                    <AddRow label="adicionar filtro" onClick={() => setAdding((v) => !v)} refEl={addBtnRef} />
                    {adding && (
                        <div
                            style={{
                                position: 'absolute',
                                zIndex: 40,
                                left: 0,
                                right: 0,
                                marginTop: 4,
                                background: T.bg,
                                border: `1px solid ${T.line}`,
                                borderRadius: 4,
                                boxShadow: '0 6px 18px rgba(0,0,0,0.10)',
                                padding: 4,
                            }}
                        >
                            {availableToAdd.map((opt) => (
                                <button
                                    key={opt.id}
                                    type="button"
                                    onClick={() => {
                                        setAdding(false);
                                        // Stock rule por provider: anexa nova com o provider
                                        // certo já fixado + abre editor dela (stock_rule:idx).
                                        if (opt.provider) {
                                            const nextRules = [
                                                ...stockRules,
                                                MakeDefaultStockRule(opt.provider, warehouses),
                                            ];
                                            onChange({ ...f, stock_rules: nextRules });
                                            const newIdx = nextRules.length - 1;
                                            setTimeout(() => setEditing(`stock_rule:${newIdx}`), 30);
                                            return;
                                        }
                                        // Demais: period inicia em last_30d, multi vazios.
                                        const init =
                                            opt.id === 'period'
                                                ? { period: 'last_30d' }
                                                : opt.id === 'brands'
                                                  ? { brands: [] }
                                                  : { tags: [] };
                                        onChange({ ...f, ...init });
                                        setTimeout(() => setEditing(opt.id), 30);
                                    }}
                                    style={{
                                        display: 'block',
                                        width: '100%',
                                        padding: '6px 10px',
                                        fontSize: 12,
                                        textAlign: 'left',
                                        background: 'transparent',
                                        border: 'none',
                                        cursor: 'pointer',
                                        color: T.ink,
                                        outline: 'none',
                                    }}
                                >
                                    {opt.label}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

/**
 * FilterSection — wrapper PSection do FixedFilters. Consome o context pra
 * pegar draft/filters/opts; toda lógica de listar/adicionar/editar/remover
 * filtros mora em FixedFilters (componente interno).
 */
export function FilterSection() {
    const { draft, patchFilters, brandOpts, tagOpts, warehouses } = usePresetBuilder();

    return (
        <PSection title="Filtros fixos" hint="Aplicados automaticamente quando o preset é usado.">
            <FixedFilters
                draft={draft}
                onChange={patchFilters}
                brandOpts={brandOpts}
                tagOpts={tagOpts}
                warehouses={warehouses}
            />
        </PSection>
    );
}
